import React from 'react';
import { WebsiteDesignProps } from './types';
import { Pill, FlaskConical, ClipboardCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";

const PharmacySchool: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "College of Pharmacy";

    return (
        <div className="bg-white min-h-screen font-sans text-slate-800">
            <nav className="bg-[#4a148c] text-white px-8 py-4">
                <div className="container mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3 font-bold text-xl">
                        <Pill className="text-[#ce93d8]" /> {schoolName}
                    </div>
                    <div className="hidden md:flex gap-8 text-sm font-medium text-white/80">
                        <a href="#" className="hover:text-white transition-colors">PharmD</a>
                        <a href="#" className="hover:text-white transition-colors">Research</a>
                        <a href="#" className="hover:text-white transition-colors">Residencies</a>
                        <a href="#" className="hover:text-white transition-colors">Alumni</a>
                    </div>
                    <Button className="bg-white text-[#4a148c] hover:bg-[#f3e5f5] font-bold rounded px-6">
                        Apply
                    </Button>
                </div>
            </nav>

            <header className="bg-gradient-to-br from-[#4a148c] to-[#7b1fa2] text-white py-20 px-8">
                <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="inline-block bg-white/10 backdrop-blur border border-white/20 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
                            #5 Ranked Pharmacy School
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                            Advancing <br/> <span className="text-[#ce93d8]">Human Health.</span>
                        </h1>
                        <p className="text-lg text-white/80 mb-10 max-w-lg leading-relaxed">
                            Train to be a clinical pharmacist, researcher, or industry leader. Our rigorous curriculum prepares you for the future of medicine.
                        </p>
                        <div className="flex gap-4">
                            <Button className="bg-white text-[#4a148c] hover:bg-[#f3e5f5] font-bold px-8 h-12 rounded-lg">
                                PharmD Program
                            </Button>
                            <Button variant="outline" className="border-white text-white hover:bg-white/10 font-bold px-8 h-12 rounded-lg">
                                PhD Research
                            </Button>
                        </div>
                    </div>

                    <div className="hidden md:flex justify-end">
                        <div className="grid grid-cols-2 gap-4 max-w-sm">
                            <div className="bg-white/10 backdrop-blur border border-white/20 p-6 rounded-xl text-center">
                                <div className="text-3xl font-bold mb-1">4</div>
                                <div className="text-xs uppercase tracking-wider text-white/60">Year Program</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur border border-white/20 p-6 rounded-xl text-center">
                                <div className="text-3xl font-bold mb-1">96%</div>
                                <div className="text-xs uppercase tracking-wider text-white/60">NAPLEX Pass</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur border border-white/20 p-6 rounded-xl text-center">
                                <div className="text-3xl font-bold mb-1">$125k</div>
                                <div className="text-xs uppercase tracking-wider text-white/60">Avg Salary</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur border border-white/20 p-6 rounded-xl text-center">
                                <div className="text-3xl font-bold mb-1">50+</div>
                                <div className="text-xs uppercase tracking-wider text-white/60">Residency Sites</div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <section className="py-20 container mx-auto px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Pill, title: "Clinical Pharmacy", desc: "Patient-centered care in hospital and community settings.", color: "bg-purple-50 text-purple-600" },
                        { icon: FlaskConical, title: "Pharmaceutical Sciences", desc: "Drug discovery, formulation, and delivery systems.", color: "bg-blue-50 text-blue-600" },
                        { icon: ClipboardCheck, title: "Public Health", desc: "Population health, policy, and regulatory affairs.", color: "bg-green-50 text-green-600" },
                    ].map((item, i) => (
                        <div key={i} className="p-8 rounded-xl border border-slate-100 hover:shadow-lg transition-shadow">
                            <div className={`w-14 h-14 rounded-xl ${item.color} flex items-center justify-center mb-6`}>
                                <item.icon size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                            <p className="text-slate-500 leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default PharmacySchool;
